import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

// Chart-block renderer. The bot opts into chart rendering by writing a fenced
// code block with language `chart` containing a small JSON spec:
//
//   ```chart
//   {"type":"bar","title":"المناقصات حسب السنة","labels":["2020","2021","2022"],"values":[786,1963,1706]}
//   ```
//
// Supported types: bar | line | pie. For paired/comparison values, use:
//   {"type":"bar","labels":[...],"series":[{"name":"Count","values":[...]},{"name":"Value SAR M","values":[...]}]}

type Series = { name: string; values: number[] };
type ChartSpec = {
  type: 'bar' | 'line' | 'pie';
  title?: string;
  labels?: string[];
  values?: number[];
  series?: Series[];
  unit?: string;
};

const COLORS = ['#19355C', '#22A063', '#C8A249', '#7B8AB8', '#5E7AA3', '#A35A35'];

function tryParseSpec(text: string): ChartSpec | null {
  try {
    const trimmed = text.trim();
    if (!trimmed.startsWith('{')) return null;
    const j = JSON.parse(trimmed);
    if (!j || typeof j !== 'object') return null;
    if (j.type !== 'bar' && j.type !== 'line' && j.type !== 'pie') return null;
    if (!Array.isArray(j.labels)) return null;
    if (!Array.isArray(j.values) && !Array.isArray(j.series)) return null;
    return j as ChartSpec;
  } catch {
    return null;
  }
}

export function maybeRenderChart(text: string): { node: React.ReactNode; raw?: string } | null {
  const spec = tryParseSpec(text);
  if (!spec) return null;

  // Build Recharts data: each row is one bucket (label), with series fields.
  const seriesList: Series[] = spec.series ?? [{ name: spec.unit || 'Value', values: spec.values ?? [] }];
  const data = (spec.labels ?? []).map((lbl, i) => {
    const row: Record<string, number | string> = { label: lbl };
    for (const s of seriesList) row[s.name] = s.values[i] ?? 0;
    return row;
  });

  const HEIGHT = 280;

  let chartEl: React.ReactNode;
  if (spec.type === 'bar') {
    chartEl = (
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {seriesList.map((s, i) => (
            <Bar key={s.name} dataKey={s.name} fill={COLORS[i % COLORS.length]} radius={[6, 6, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  } else if (spec.type === 'line') {
    chartEl = (
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {seriesList.map((s, i) => (
            <Line key={s.name} type="monotone" dataKey={s.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  } else {
    // Pie: only first series is used.
    const pieData = data.map((d) => ({ name: d.label as string, value: d[seriesList[0].name] as number }));
    chartEl = (
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <PieChart>
          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
            {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return {
    node: (
      <div className="my-3 rounded-xl bg-tatweer-light/50 ring-1 ring-tatweer-line p-3" dir="ltr">
        {spec.title && <div className="text-xs font-semibold text-tatweer-navy mb-2 text-center">{spec.title}</div>}
        {chartEl}
      </div>
    ),
  };
}
